import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Waypoints } from 'lucide-react';
import { DependencyProposals } from '@apm/contracts';
import { AiSkillPanel } from '../../ai/components/ai-skill-panel';

/**
 * Suy ra phụ thuộc còn thiếu — APM-AI-j, nút ở chế độ Gantt.
 *
 * ## Vì sao chỉ đọc, không có nút "nối hết"
 *
 * Mỗi đề xuất phải trích **một câu có thật** làm dẫn chứng (`evidence`), và
 * người đọc cần đọc câu đó trước khi đồng ý. Một nút nối hàng loạt biến việc
 * đọc dẫn chứng thành tuỳ chọn, và phụ thuộc sai thì đường găng sai — sai ở chỗ
 * không ai nghĩ để kiểm lại.
 *
 * Nối thật vẫn làm bằng cách kéo giữa hai thanh trên Gantt, đúng đường mà
 * `manualAlternative` của skill trỏ tới.
 */
export function InferDependenciesSection({
  projectId,
  defaultOpen = false,
}: {
  readonly projectId: string;
  /** Mở sẵn khi đây là **cả nội dung** của nơi nó đứng — hộp thoại "Sửa nhanh". */
  readonly defaultOpen?: boolean;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <AiSkillPanel
      skill="infer_dependencies"
      projectId={projectId}
      icon={Waypoints}
      title={t('inferDeps.title')}
      help={t('inferDeps.help')}
      label={t('inferDeps.run')}
      prompt={t('inferDeps.prompt')}
      schema={DependencyProposals}
      routeParams={{ projectId }}
      defaultOpen={defaultOpen}
      render={(content) => (
        <div className="flex flex-col gap-3">
          {content.proposals.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('inferDeps.none')}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {content.proposals.map((proposal, index) => (
                <li key={index} className="rounded-DEFAULT border border-border bg-background p-2">
                  <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="rounded-control bg-muted px-1.5 py-0.5">
                      {t(`gantt.dependencyType.${proposal.kind}`)}
                    </span>
                    <span>{t(`ai.estimate.confidence.${proposal.confidence}`)}</span>
                  </div>
                  <p className="mt-1 text-sm">{proposal.reason}</p>
                  {/* Dẫn chứng là câu có thật trong dữ liệu — đặt ngay dưới lý
                      do, không giấu sau một nút. */}
                  <p className="mt-1 border-l-2 border-border pl-2 text-xs italic text-muted-foreground">
                    {proposal.evidence}
                  </p>
                </li>
              ))}
            </ul>
          )}

          {content.cyclesDetected.length > 0 ? (
            <div className="rounded-DEFAULT border border-rag-red/40 bg-rag-red/5 p-2">
              <p className="text-xs font-medium text-rag-red">
                {t('inferDeps.cycles', { count: content.cyclesDetected.length })}
              </p>
              <ul className="mt-1 flex list-disc flex-col gap-0.5 pl-5 text-xs">
                {content.cyclesDetected.map((cycle, index) => (
                  <li key={index}>{cycle.reason}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    />
  );
}
